/**
 * The makeup of a section to be used by our SectionRenderer
 *
 * @typedef {Object} Section
 * @property {string} id - The name of the section. This will be the filename of the section
 * @property {string} section - The ID of the section. This can be uniquely generated by Shopify
 *   for a repeatable section
 * @property {string} selectors - The CSS selector which contains the sections content
 * @property {string} [html] - This is the HTML returned by the section rendering API
 * @property {Function} [convertor] - This will be ran on the new HTML before it's inserted into the
 * @property {boolean} [dontRender] - Sometimes we want to get a section back but no render it, like
 *   a cart count DOM
 */
window.BAO.sectionRenderer = new (class SectionRenderer {
  /**
   * Request a section, or sections, from the section rendering API
   *
   * @param {string|string[]} sections - The names of the sections we want to fetch
   * @param {string} [sectionsUrl] - The context in which we want the sections to be
   *   rendered in
   *
   * @returns {Promise<Object>}
   */
  async fetch (sections, sectionsUrl = this.sectionsUrl) {
    if (typeof sections === 'string') {
      sections = [sections]
    }

    const [path, querystring = ''] = sectionsUrl.split('?')

    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(querystring)
    params.append('sections', sections.join(','))

    const section = await fetch(`${path}?${params.toString()}`)

    return section.json()
  }

  /**
   * Request a single section from the section rendering API
   * https://shopify.dev/api/section-rendering#request-a-single-section
   *
   * @param {string} section - The names of the section we want to fetch
   * @param {string} [sectionsUrl] - The context in which we want the sections to be
   *   rendered in
   *
   * @returns {Promise<string>}
   */
  async fetchSingle (section, sectionsUrl = window.location.pathname) {
    const [path, querystring = ''] = sectionsUrl.split('?')

    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(querystring)
    params.set('section_id', section)

    const response = await fetch(`${path}?${params.toString()}`)

    return response.text()
  }

  /**
   * Render a section returned from the section rendering API
   *
   * @param {Section} section - The makeup of the section we want to render
   *
   * @returns {void}
   */
  render (section) {
    if ('dontRender' in section && section.dontRender) return

    this.validate(section)

    section.selectors.forEach((selector) => {
      const elementToReplace = document
        .getElementById(section.id)
        ?.querySelector(selector)

      if (elementToReplace) {
        const newHTML = this.getOuterHtml(section.html, {
          id: section.id,
          selector,
        })
        const oldHTML = elementToReplace.outerHTML

        elementToReplace.outerHTML =
          typeof section.convertor === 'function'
            ? section.convertor(oldHTML, newHTML)
            : newHTML
      }
    })
  }

  /**
   * Render multiple sections returned from the section rendering API
   *
   * @param {Section[]} sections - The sections we want to render
   *
   * @returns {void}
   */
  renderMultiple (sections) {
    sections.forEach((section) => this.render(section))
  }

  /**
   * Grab the HTML that is inside the section otherwise we end up with section inception
   *
   * @param {string} html - The section HTML returned from the section rendering API
   * @param {{ id: string, selector: string }} selector
   *
   * @returns {string}
   */
  getOuterHtml (html, { id = '', selector = '.shopify-section' } = {}) {
    const dom = this.convertToDOM(html)
    const element =
      dom.getElementById(id)?.querySelector(selector) || dom.getElementById(id)

    return element?.outerHTML
  }

  /**
   * Convert a HTML string into a parseable DOM
   *
   * @param {string} html
   *
   * @returns {Document}
   */
  convertToDOM (html) {
    return new DOMParser().parseFromString(html, 'text/html')
  }

  /**
   * Render a section returned from the section rendering API
   *
   * @param {Section} section - The makeup of the section we want to render
   *
   * @returns {boolean}
   */
  validate (section) {
    const isValid = this.expectedKeys.every((key) => key in section)

    if (!isValid) {
      throw new Error(
        `section is missing these keys: "${this.expectedKeys
          .filter((k) => !(k in section))
          .join(', ')}"`,
      )
    }

    return isValid
  }

  get expectedKeys () {
    return ['id', 'selectors', 'html']
  }

  /**
   * @returns {Section[]}
   */
  get pushCartSections () {
    return [
      {
        id: 'push-cart',
        section: document.getElementById('push-cart').dataset.id,
        selectors: [
          '[data-push-cart-el="shipping"]',
          '[data-push-cart-el="items"]',
          '[data-push-cart-el="shopifyRecommendations"]',
          '[data-push-cart-el="discounts"]',
          '[data-push-cart-el="gift-with-purchase"]',
          '[data-push-cart-el="footer"]',
        ],
      },
      {
        id: 'push-cart-count',
        section: 'push-cart-count',
        selectors: ['.shopify-section'],
        dontRender: true,
      },
    ]
  }

  /**
   * @returns {Section[]}
   */
  get cartSections () {
    return [
      {
        id: 'push-cart-count',
        section: 'push-cart-count',
        selectors: ['.shopify-section'],
        dontRender: true,
      },
      {
        id: 'template-cart',
        section: document.getElementById('template-cart').dataset.id,
        selectors: ['[data-cart-el="items"]', '[data-cart-el="summary"]'],
      },
    ]
  }

  /**
   * @returns {string} The current url pathname and query parameters
   */
  get sectionsUrl () {
    return `${window.location.pathname}${window.location.search}`
  }
})()
